import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ConfirmDialogComponent } from 'shared/dialogs/confirm-dialog/confirm-dialog.component';
import { KsefAdminApiService } from 'shared/services/api/ksef-admin.api.service';
import { ToastService } from 'shared/services/toast.service';
import { NotificationData } from 'fakturnia-shared';

/**
 * KSeF Tools Component - Narzędzia administracyjne
 * 
 * Funkcje:
 * - Usuwanie danych testowych środowiska KSeF
 * - Migracja Unified Document Store (lazy update pól dokumentów)
 */
@Component({
  selector: 'app-ksef-tools',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './tools.component.html',
  styleUrl: './tools.component.scss'
})
export class KsefToolsComponent implements OnInit {
  
  isClearing = false;
  lastResult: {
    ksefInvoicesDeleted: number;
    batchSessionsDeleted: number;
    documentsCleared: number;
    activityLogsMarkedDeleted: number;
  } | null = null;

  isRunningLazyUpdate = false;
  lazyUpdateResult: {
    documentsToUpdate: number;
    step1_defaults: number;
    step2_expenses: number;
    step3_ksefSynced: number;
    step4_nonKsef: number;
    durationMs?: number;
  } | null = null;

  // Test Invoice
  testInvoiceJson = '';
  isSendingTestInvoice = false;
  testInvoiceResult: any = null;
  testInvoiceError: string | null = null;

  // Środowisko - blokady produkcyjne
  isEnvironmentLoading = true;
  isTestSendBlocked = true;
  blockReason: string | null = null;
  ksefEnvironment: string | null = null;
  hasKsefConnection = false;

  constructor(
    private _dialog: MatDialog,
    private _ksefAdminApi: KsefAdminApiService,
    private _toast: ToastService
  ) {}

  ngOnInit(): void {
    this.loadEnvironmentInfo();
  }

  /**
   * Potwierdzenie i wykonanie czyszczenia danych testowych
   */
  confirmClearTestData(): void {
    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Usuń dane testowe KSeF',
        message: 'Czy na pewno chcesz usunąć wszystkie dane z testowego środowiska KSeF? Ta operacja jest nieodwracalna.',
        confirmText: 'Usuń dane testowe',
        cancelText: 'Anuluj',
        confirmColor: 'warn'
      },
      width: '450px'
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.clearTestData();
      }
    });
  }

  /**
   * Wykonanie czyszczenia danych testowych
   */
  private clearTestData(): void {
    this.isClearing = true;
    this.lastResult = null;

    this._ksefAdminApi.clearTestData().subscribe({
      next: (response: any) => {
        this.isClearing = false;
        this.lastResult = response.data;
        this._toast.showToast(new NotificationData({
          icon: 'tabler_outline:check',
          title: 'Sukces',
          status: 'success',
          message: 'Dane testowe zostały usunięte pomyślnie',
          timeToClose: 5000
        }));
      },
      error: (error) => {
        this.isClearing = false;
        console.error('Błąd podczas usuwania danych testowych:', error);
        this._toast.showToast(new NotificationData({
          icon: 'tabler_outline:x',
          title: 'Błąd',
          status: 'danger',
          message: 'Wystąpił błąd podczas usuwania danych testowych',
          timeToClose: 5000
        }));
      }
    });
  }

  /**
   * Potwierdzenie i uruchomienie lazy update document fields
   */
  confirmRunLazyUpdate(): void {
    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Migracja Unified Document Store',
        message: 'Czy chcesz uruchomić migrację pól dokumentów (source, direction, isKsefRequired, isKsefSynced)? Operacja zaktualizuje dokumenty, które nie mają jeszcze nowych pól.',
        confirmText: 'Uruchom migrację',
        cancelText: 'Anuluj',
        confirmColor: 'primary'
      },
      width: '450px'
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.runLazyUpdate();
      }
    });
  }

  /**
   * Wykonanie lazy update document fields
   */
  private runLazyUpdate(): void {
    this.isRunningLazyUpdate = true;
    this.lazyUpdateResult = null;

    this._ksefAdminApi.runLazyUpdateDocumentFields().subscribe({
      next: (response: any) => {
        this.isRunningLazyUpdate = false;
        this.lazyUpdateResult = response.data;
        this._toast.showToast(new NotificationData({
          icon: 'tabler_outline:check',
          title: 'Sukces',
          status: 'success',
          message: response.message || 'Migracja zakończona pomyślnie',
          timeToClose: 5000
        }));
      },
      error: (error) => {
        this.isRunningLazyUpdate = false;
        console.error('Błąd podczas migracji dokumentów:', error);
        this._toast.showToast(new NotificationData({
          icon: 'tabler_outline:x',
          title: 'Błąd',
          status: 'danger',
          message: 'Wystąpił błąd podczas migracji dokumentów',
          timeToClose: 5000
        }));
      }
    });
  }

  /**
   * Pobierz informacje o środowisku KSeF admina
   */
  private loadEnvironmentInfo(): void {
    this.isEnvironmentLoading = true;

    this._ksefAdminApi.getAdminKsefEnvironment().subscribe({
      next: (response: any) => {
        this.isEnvironmentLoading = false;
        this.isTestSendBlocked = response.data.isBlocked;
        this.blockReason = response.data.blockReason;
        this.ksefEnvironment = response.data.ksefEnvironment;
        this.hasKsefConnection = response.data.hasConnection;
      },
      error: () => {
        this.isEnvironmentLoading = false;
        this.isTestSendBlocked = true;
        this.blockReason = 'Nie udało się sprawdzić środowiska KSeF';
      }
    });
  }

  /**
   * Walidacja JSON i wysłanie testowej faktury
   */
  confirmSendTestInvoice(): void {
    // Walidacja JSON
    let parsedDocument: any;
    try {
      parsedDocument = JSON.parse(this.testInvoiceJson);
    } catch {
      this._toast.showToast(new NotificationData({
        icon: 'tabler_outline:x',
        title: 'Błąd',
        status: 'danger',
        message: 'Nieprawidłowy format JSON. Sprawdź składnię.',
        timeToClose: 5000
      }));
      return;
    }

    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Wyślij testową fakturę do KSeF',
        message: `Czy na pewno chcesz wysłać tę fakturę testowo do KSeF (środowisko: ${this.ksefEnvironment})? Faktura zostanie wysłana przez Twoje połączenie KSeF. Żadne dane w bazie nie zostaną zmienione.`,
        confirmText: 'Wyślij testowo',
        cancelText: 'Anuluj',
        confirmColor: 'primary'
      },
      width: '450px'
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.sendTestInvoice(parsedDocument);
      }
    });
  }

  /**
   * Wysłanie testowej faktury do KSeF
   */
  private sendTestInvoice(document: any): void {
    this.isSendingTestInvoice = true;
    this.testInvoiceResult = null;
    this.testInvoiceError = null;

    this._ksefAdminApi.sendTestInvoice(document).subscribe({
      next: (response: any) => {
        this.isSendingTestInvoice = false;
        this.testInvoiceResult = response.data;
        this._toast.showToast(new NotificationData({
          icon: response.data.success ? 'tabler_outline:check' : 'tabler_outline:alert-triangle',
          title: response.data.success ? 'Sukces' : 'Wynik testu',
          status: response.data.success ? 'success' : 'warning',
          message: response.message,
          timeToClose: 8000
        }));
      },
      error: (error: any) => {
        this.isSendingTestInvoice = false;
        this.testInvoiceError = error.error?.message || error.message || 'Nieznany błąd';
        this._toast.showToast(new NotificationData({
          icon: 'tabler_outline:x',
          title: 'Błąd',
          status: 'danger',
          message: this.testInvoiceError!,
          timeToClose: 8000
        }));
      }
    });
  }
}
